import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Package,
  Download,
  FileJson,
  FileSpreadsheet,
  Loader2,
  PieChart,
  Scale,
  AlertTriangle,
} from "lucide-react";
import { SkeletonTable } from "./Skeleton";

interface SBOMStatistics {
  format: string;
  spec_version: string;
  total_components?: number;
  total_packages?: number;
  by_type?: Record<string, number>;
  by_ecosystem?: Record<string, number>;
  by_license?: Record<string, number>;
  generated_at?: string;
  last_generated?: string;
}

interface SBOMResponse {
  id: string;
  repo_id: string;
  format: string;
  spec_version: string;
  component_count: number;
  statistics: SBOMStatistics;
  created_at: string;
  sbom?: unknown;
}

interface SBOMStatsResponse {
  repo_id: string;
  available_formats: string[];
  sboms: Record<string, {
    spec_version: string;
    component_count: number;
    statistics: SBOMStatistics;
    last_generated: string;
  }>;
}

interface SBOMTabProps {
  repoId: string;
}

async function fetchSBOMStats(repoId: string): Promise<SBOMStatsResponse> {
  const { apiFetch } = await import("../../api/client");
  try {
    return await apiFetch<SBOMStatsResponse>({
      path: `/v1/repos/${repoId}/sbom/statistics`,
    });
  } catch (e: any) {
    if (e?.status === 404) {
      return { repo_id: repoId, available_formats: [], sboms: {} };
    }
    throw e;
  }
}

async function downloadSBOM(repoId: string, format: "cyclonedx" | "spdx" | "csv"): Promise<void> {
  // Download needs raw fetch for blob handling
  const { API_BASE_URL } = await import("../../api/client");
  const response = await fetch(`${API_BASE_URL}/v1/repos/${repoId}/sbom?format=${format}`, {
    credentials: "include",
  });
  
  if (!response.ok) {
    // statusText is empty over HTTP/2, which is what made the failed CSV
    // download read as "Failed to download SBOM: " with nothing after it.
    let detail = "";
    try {
      const body = await response.json();
      detail = body?.message || body?.error || "";
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Download failed (HTTP ${response.status}).`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Use the suffix each specification's tooling recognises, so the downloaded
  // file can be handed straight to a validator.
  const suffix = format === "csv" ? "csv" : format === "spdx" ? "spdx.json" : "cdx.json";
  a.download = `sbom-${repoId}.${suffix}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export default function SBOMTab({ repoId }: SBOMTabProps) {
  // A rejected download used to surface only as an unhandled promise rejection
  // in the console: the button looked like it did nothing at all.
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const runDownload = React.useCallback(
    async (format: "cyclonedx" | "spdx" | "csv") => {
      setDownloadError(null);
      setDownloading(format);
      try {
        await downloadSBOM(repoId, format);
      } catch (err) {
        setDownloadError(err instanceof Error ? err.message : "Download failed.");
      } finally {
        setDownloading(null);
      }
    },
    [repoId]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["sbom-stats", repoId],
    queryFn: () => fetchSBOMStats(repoId),
    staleTime: 60000,
  });

  if (isLoading) {
    return <SkeletonTable rows={5} columns={4} />;
  }

  if (error) {
    return (
      <div className="card p-8 text-center">
        <AlertTriangle className="h-10 w-10 text-high mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-text">Failed to load SBOM</h3>
        <p className="text-sm text-muted mt-2">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
      </div>
    );
  }

  if (!data || data.available_formats.length === 0) {
    return (
      <div className="card p-12 text-center">
        <Package className="h-12 w-12 text-muted mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-text">No SBOM Available</h3>
        <p className="text-sm text-muted mt-2">
          Run a baseline scan to generate a Software Bill of Materials.
        </p>
      </div>
    );
  }

  const sbom = data.sboms.cyclonedx || data.sboms.spdx;
  const stats = sbom?.statistics || {};
  const componentCount = sbom?.component_count || stats.total_components || stats.total_packages || 0;
  const lastGenerated = sbom?.last_generated;

  // Top ecosystems
  const ecosystems = Object.entries(stats.by_ecosystem || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  // Top licenses
  const licenses = Object.entries(stats.by_license || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8);

  // Risky licenses (AGPL, GPL, SSPL, etc.)
  const riskyLicensePatterns = ["AGPL", "GPL-3", "SSPL", "BUSL", "Commons Clause"];
  const riskyLicenses = Object.entries(stats.by_license || {}).filter(([name]) =>
    riskyLicensePatterns.some((p) => name.toUpperCase().includes(p.toUpperCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header with downloads */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-text flex items-center gap-2">
            <Package className="h-5 w-5 text-brand" />
            Software Bill of Materials
          </h2>
          <p className="text-sm text-muted mt-1">
            {componentCount} components • Last generated: {lastGenerated ? new Date(lastGenerated).toLocaleDateString() : "N/A"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {data.available_formats.includes("cyclonedx") && (
            <button
              onClick={() => runDownload("cyclonedx")}
              disabled={downloading !== null}
              className="btn-ghost text-sm"
            >
              <FileJson className="h-4 w-4" />
              CycloneDX
            </button>
          )}
          {data.available_formats.includes("spdx") && (
            <button
              onClick={() => runDownload("spdx")}
              disabled={downloading !== null}
              className="btn-ghost text-sm"
            >
              <FileJson className="h-4 w-4" />
              SPDX
            </button>
          )}
          <button
            onClick={() => runDownload("csv")}
            disabled={downloading !== null}
            className="btn-ghost text-sm"
          >
            <FileSpreadsheet className="h-4 w-4" />
            CSV
          </button>
        </div>
      </div>

      {downloadError && (
        <div className="card p-4 flex items-start gap-3 border-high/40">
          <AlertTriangle className="h-4 w-4 text-high mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-text">SBOM download failed</p>
            <p className="text-sm text-muted mt-0.5">{downloadError}</p>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-2">
            <Package className="h-5 w-5 text-brand" />
            <span className="text-sm font-medium text-muted">Total Components</span>
          </div>
          <div className="text-2xl font-bold text-text">{componentCount}</div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-2">
            <PieChart className="h-5 w-5 text-info" />
            <span className="text-sm font-medium text-muted">Ecosystems</span>
          </div>
          <div className="text-2xl font-bold text-text">{ecosystems.length}</div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-2">
            <Scale className="h-5 w-5 text-medium" />
            <span className="text-sm font-medium text-muted">Unique Licenses</span>
          </div>
          <div className="text-2xl font-bold text-text">{licenses.length}</div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="h-5 w-5 text-high" />
            <span className="text-sm font-medium text-muted">Risky Licenses</span>
          </div>
          <div className="text-2xl font-bold text-high">{riskyLicenses.length}</div>
        </div>
      </div>

      {/* Ecosystem breakdown */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-5">
          <h3 className="text-sm font-medium text-text mb-4">By Ecosystem</h3>
          {ecosystems.length === 0 ? (
            <p className="text-sm text-muted">No ecosystem data available</p>
          ) : (
            <div className="space-y-3">
              {ecosystems.map(([ecosystem, count]) => {
                const percentage = Math.round((count / componentCount) * 100);
                return (
                  <div key={ecosystem}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-text capitalize">{ecosystem}</span>
                      <span className="text-muted">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-surface2 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-medium text-text mb-4">License Distribution</h3>
          {licenses.length === 0 ? (
            <p className="text-sm text-muted">No license data available</p>
          ) : (
            <div className="space-y-2">
              {licenses.map(([license, count]) => {
                const isRisky = riskyLicensePatterns.some((p) =>
                  license.toUpperCase().includes(p.toUpperCase())
                );
                return (
                  <div
                    key={license}
                    className={`flex justify-between text-sm px-3 py-2 rounded-lg ${
                      isRisky ? "bg-high/10" : "bg-surface2"
                    }`}
                  >
                    <span className={`font-medium ${isRisky ? "text-high" : "text-text"}`}>
                      {license}
                      {isRisky && <span className="ml-2 text-xs">⚠️</span>}
                    </span>
                    <span className="text-muted">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Format info */}
      <div className="card p-5 bg-surface2/50">
        <h3 className="text-sm font-medium text-text mb-2">Available Formats</h3>
        <div className="flex flex-wrap gap-2">
          {data.available_formats.map((format) => (
            <span
              key={format}
              className="inline-flex items-center gap-1 px-2 py-1 bg-brand/10 text-brand rounded text-xs font-medium"
            >
              <FileJson className="h-3 w-3" />
              {format.toUpperCase()}
            </span>
          ))}
        </div>
        <p className="text-xs text-muted mt-3">
          SBOMs are generated from the scanned repository state and comply with industry standards for software supply chain transparency.
        </p>
      </div>
    </div>
  );
}
